import React, { useEffect, useMemo, useRef, useState } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import routes from "../../routes";
import NotFound from "../../Views/NotFound/NotFound";
import Sidebar from "../Sidebar";
import { MainNav } from "../MuiTable";
import { useMediaQuery } from "@mui/material";
import { Grid } from "@mui/material";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import {
  AdminDarkModeProvider,
  useAdminDarkMode,
} from "../../contexts/AdminDarkModeContext";
import {
  CJ_PORTAL_SIDEBAR_COLLAPSED_PX,
  CJ_PORTAL_SIDEBAR_EXPANDED_PX,
} from "../../utils/cjPortalSidebarMetrics";
import {
  updateCompanyNameFromToken,
  setPageTitle,
  getPageTitleFromRoute,
} from "../../utils/pageTitle";
import "./style.css";
import "./admin-dark-overrides.css";

function SuperAdminInner() {
  const { darkMode } = useAdminDarkMode();
  const mainContent = useRef(null);
  const location = useLocation();
  const [isSidebarClosed, setIsSidebarClosed] = useState(false);
  const [isSidebarDisplay, setIsSidebarDisplay] = useState(true);
  const isMediumScreen = useMediaQuery("(max-width:767px)", { noSsr: true });

  useEffect(() => {
    if (!isMediumScreen) {
      setIsSidebarDisplay(true);
      setIsSidebarClosed(false);
    } else {
      setIsSidebarDisplay(false);
    }
  }, [isMediumScreen]);

  useEffect(() => {
    const init = async () => {
      await updateCompanyNameFromToken();
      setPageTitle(getPageTitleFromRoute(location.pathname));
    };
    init();
  }, []);

  useEffect(() => {
    if (darkMode) {
      document.body.classList.add("admin-dark-active");
    } else {
      document.body.classList.remove("admin-dark-active");
    }
    return () => document.body.classList.remove("admin-dark-active");
  }, [darkMode]);

  useEffect(() => {
    document.documentElement.scrollTop = 0;
    document.scrollingElement.scrollTop = 0;
    if (mainContent.current) {
      mainContent.current.scrollTop = 0;
    }
    setPageTitle(getPageTitleFromRoute(location.pathname));
  }, [location]);

  const superAdminTheme = useMemo(
    () =>
      createTheme({
        palette: {
          mode: darkMode ? "dark" : "light",
          primary: {
            main: darkMode ? "#90caf9" : "#1976d2",
          },
          secondary: {
            main: darkMode ? "#f48fb1" : "#dc004e",
          },
          background: {
            default: darkMode ? "#0f1419" : "#f7f8fa",
            paper: darkMode ? "#1a2332" : "#ffffff",
          },
          ...(darkMode
            ? {
                text: {
                  primary: "#e8eaed",
                  secondary: "#9aa0a6",
                },
                divider: "#2d3b4e",
              }
            : {}),
        },
        components: {
          MuiPaper: {
            styleOverrides: {
              root: {
                backgroundImage: "none",
              },
            },
          },
        },
        typography: {
          fontFamily: "'Poppins', sans-serif",
          h1: {
            fontFamily: "'Poppins', sans-serif",
            fontWeight: 600,
          },
          h2: {
            fontFamily: "'Poppins', sans-serif",
            fontWeight: 500,
          },
          body1: {
            fontFamily: "'Poppins', sans-serif",
          },
        },
      }),
    [darkMode]
  );

  const getRoutes = (routes) => {
    return routes?.map((prop, key) => {
      if (prop.layout === "/superadmin" && !prop.isCollapse) {
        return (
          <Route path={prop?.path} element={prop?.component} key={key} exact />
        );
      }
      if (prop.children) {
        return prop.children?.map((item) => (
          <Route
            path={item?.path}
            element={item?.component}
            key={key}
            exact
          />
        ));
      }
      return null;
    });
  };

  return (
    <ThemeProvider theme={superAdminTheme}>
      <Grid
        className={`admin-shell${darkMode ? " admin-shell--dark" : ""}`}
        style={{
          backgroundColor: "var(--app-page-background)",
          width: "100vw",
          height: "100vh",
          overflow: "auto",
          overflowX: "hidden",
        }}
      >
        <Sidebar
          layout="/superadmin"
          isSidebarClosed={isSidebarClosed}
          setIsSidebarClosed={setIsSidebarClosed}
          isSidebarDisplay={isSidebarDisplay}
          setIsSidebarDisplay={setIsSidebarDisplay}
        />

        <MainNav
          layout="/superadmin"
          isSidebarDisplay={isSidebarDisplay}
          setIsSidebarDisplay={setIsSidebarDisplay}
          isSidebarClosed={isSidebarClosed}
        />
        <Grid
          className="main-content invoicePaidSlip"
          ref={mainContent}
          sx={{
            boxSizing: "border-box",
            overflowX: "hidden",
            marginLeft: !isMediumScreen
              ? isSidebarClosed
                ? `${CJ_PORTAL_SIDEBAR_COLLAPSED_PX}px`
                : `${CJ_PORTAL_SIDEBAR_EXPANDED_PX}px`
              : 0,
            width: !isMediumScreen
              ? `calc(100% - ${
                  isSidebarClosed
                    ? CJ_PORTAL_SIDEBAR_COLLAPSED_PX
                    : CJ_PORTAL_SIDEBAR_EXPANDED_PX
                }px)`
              : "100%",
            maxWidth: !isMediumScreen
              ? `calc(100% - ${
                  isSidebarClosed
                    ? CJ_PORTAL_SIDEBAR_COLLAPSED_PX
                    : CJ_PORTAL_SIDEBAR_EXPANDED_PX
                }px)`
              : "100%",
            minWidth: 0,
            px: { xs: "15px", sm: "15px", md: 0 },
            pb: 2,
            transition: "margin 0.3s ease, width 0.3s ease, max-width 0.3s ease",
          }}
        >
          <Grid
            sx={{
              width: { xs: "100%", md: "98%" },
              maxWidth: "100%",
              boxSizing: "border-box",
              minWidth: 0,
              mx: { xs: 0, md: "auto" },
            }}
            className="customerDetail_paid"
          >
            <React.Suspense fallback={null}>
              <Routes>
                {getRoutes(routes)}
                {/* Legacy alias: sidebar resolves to /superadmin/email; old links to
                    /superadmin/mail-service should not 404. */}
                <Route
                  path="/mail-service"
                  element={<Navigate to="/superadmin/email" replace />}
                />
                {/* JOB-3220 + JOB-3225: real 404 instead of silent redirect */}
                <Route path="*" element={<NotFound homePath="/superadmin/index" />} />
              </Routes>
            </React.Suspense>
          </Grid>
        </Grid>
      </Grid>
    </ThemeProvider>
  );
}

const SuperAdmin = () => (
  <AdminDarkModeProvider persistence="superadmin">
    <SuperAdminInner />
  </AdminDarkModeProvider>
);

export default SuperAdmin;
